import { observer } from 'mobx-react-lite';
import { getScenarioActions } from '../actions/getScenarioActions';
import { isSelectedCalendarEvent } from '../selectors/agendaStoreSelectors';
import { getUpNextCalendarEventId } from '../selectors/upNextEventCalendarCacheSelectors';
import type { IFocusZone } from '@fluentui/react/lib/FocusZone';
import { getCalendarEventColorScheme } from 'owa-calendar-color-resolver';
import * as React from 'react';
import {
    AgendaEventViewAllDay,
    AgendaEventViewUpNext,
    AgendaEventViewNormal,
} from 'owa-agenda-event-views-wrappers';
import { userInteractionAction } from 'owa-calendar-actions';
import {
    CalendarEventInteraction,
    CalendarEventInteractionCallback,
    KeyboardCalendarEventInteraction,
} from 'owa-calendar-event-interaction-schema';
import { AgendaItem, convertScenarioIdToViewType } from 'owa-agenda-events-common';

export interface AgendaEventViewControllerProps {
    agendaItem: AgendaItem;
    scenarioId: string;
    listRef: React.RefObject<IFocusZone>;
    className?: string;
    callback?: CalendarEventInteractionCallback;
}

export const AgendaEventViewController = observer(function AgendaEventViewController(
    props: AgendaEventViewControllerProps
) {
    const onClick = evt => {
        const { agendaItem, scenarioId, callback } = props;
        if (agendaItem.isLocalLie) {
            return;
        }
        const { updateSelectedCalendarEvent } = getScenarioActions(scenarioId);
        updateSelectedCalendarEvent(agendaItem.id.Id);
        callback?.({
            type: CalendarEventInteraction.Click,
            id: agendaItem.id,
            target: evt.currentTarget,
        });
    };
    const onDoubleClick = evt => {
        const { agendaItem, scenarioId, callback } = props;
        if (agendaItem.isLocalLie) {
            return;
        }
        const { updateSelectedCalendarEvent } = getScenarioActions(scenarioId);
        updateSelectedCalendarEvent(agendaItem.id.Id);
        callback?.({
            type: CalendarEventInteraction.DoubleClick,
            id: agendaItem.id,
            target: evt.currentTarget,
        });
    };
    const onRightClick = evt => {
        const { agendaItem, scenarioId, callback } = props;
        if (agendaItem.isLocalLie) {
            return;
        }
        const { updateSelectedCalendarEvent } = getScenarioActions(scenarioId);
        updateSelectedCalendarEvent(agendaItem.id.Id);
        evt.persist();
        evt.stopPropagation();
        evt.preventDefault();
        callback?.({
            type: CalendarEventInteraction.RightClick,
            id: agendaItem.id,
            parentFolderId: agendaItem.parentFolderId.Id,
            evt: evt,
        });
    };
    const onItemKeyDown = (target, keyboardCalendarEventInteraction) => {
        const { agendaItem, scenarioId, callback } = props;
        if (agendaItem.isLocalLie) {
            return;
        }
        const { updateSelectedCalendarEvent } = getScenarioActions(scenarioId);
        updateSelectedCalendarEvent(agendaItem.id.Id);

        callback?.({
            type: CalendarEventInteraction.KeyDown,
            id: agendaItem.id,
            target: target,
            keyboardCalendarEventInteraction: keyboardCalendarEventInteraction,
            interactionCallback: {
                [KeyboardCalendarEventInteraction.Delete]: setFocusOnAgendaList,
            },
        });
    };
    const setFocusOnAgendaList = () => {
        const { listRef } = props;
        listRef.current?.focus(true);
    };
    const onJoinOnlineMeetingClick = evt => {
        const { agendaItem, scenarioId } = props;

        evt.stopPropagation();

        userInteractionAction(
            'JoinOnlineMeeting',
            convertScenarioIdToViewType(scenarioId),
            agendaItem.isOrganizer,
            agendaItem.isMeeting,
            [agendaItem.provider]
        );
    };

    const { agendaItem, className, scenarioId } = props;
    const upNextEventId = getUpNextCalendarEventId(scenarioId);
    const viewProps = {
        onClick: onClick,
        onDoubleClick: onDoubleClick,
        onRightClick: onRightClick,
        onItemKeyDown: onItemKeyDown,
        colorScheme: getCalendarEventColorScheme({
            ParentFolderId: agendaItem.parentFolderId,
            Categories: agendaItem.categories,
        }),
        isSelected: isSelectedCalendarEvent(agendaItem.id.Id, scenarioId),
        isInteractive: !agendaItem.isLocalLie,
        className: className,
        onJoinOnlineMeetingClick: onJoinOnlineMeetingClick,
    };
    let AgendaEventView;
    if (agendaItem.isAllDay) {
        AgendaEventView = AgendaEventViewAllDay;
    } else if (upNextEventId && upNextEventId.Id === agendaItem.id.Id) {
        AgendaEventView = AgendaEventViewUpNext;
    } else {
        AgendaEventView = AgendaEventViewNormal;
    }
    return <AgendaEventView {...viewProps} {...agendaItem} />;
});
